import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import {
  allocation,
  Group as G,
  Identifiable,
  ResourceTypes,
  Allocation as A,
} from "@planzone/dto";
import {
  useAllocateResources,
  useProjectResourcesRetrieval,
  useRemoveAllocation,
  useRetrieveCurrentPlanzone,
  useRetrieveResourceGroups,
} from "@planzone/view-models";
import { ScreenWatcher } from "@common/screenWatcher";
import { LayoutWatcher } from "@common/layoutWatcher";
import { useWorkspaceContext } from "@scenes/workspaceContext";
import { SelectList } from "@common/selectList";
import { AddAllocationIcon } from "@common/assets/addAllocationIcon";
import { ResourceAllocationMenu } from "@common/resourceAllocationMenu";
import { isLastIncompleteAllocation } from "./utils";
import { Header } from "./header";
import { Allocation } from "./allocation";
import { Group } from "./group";
import { Props } from "./types";
import styles from "./styles.scss";

function sortByGroup(
  groups: Identifiable<G>[],
  allocations: Identifiable<A>[],
  type: "planzone" | "project",
): (Identifiable<G> & { allocations: Identifiable<A>[] })[] {
  return groups.reduce<
    (Identifiable<G> & { allocations: Identifiable<A>[] })[]
  >((nextGroups, group) => {
    const allocByGroup = {
      ...group,
      allocations: allocations.filter(
        ({ resource }) =>
          !!group.resources.find((r) =>
            type === "project"
              ? r.resource.uuid === resource.uuid
              : r.uuid === resource.uuid,
          ),
      ),
    };

    return allocByGroup.allocations.length > 0
      ? [...nextGroups, allocByGroup]
      : nextGroups;
  }, []);
}

function Details({ task, allocations, project, anchor, readOnly }: Props) {
  const { t } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const ctx = useWorkspaceContext();
  const isInProject = ctx.type === "project";
  const {
    isRequestPending: isPlanzoneRequestPending,
    isRequestSuccess: isPlanzoneRequestSuccess,
    isRequestFailure: isPlanzoneRequestFailure,
    groups,
    retrieveResourceGroups: retrievePlanzoneResourceGroups,
  } = useRetrieveResourceGroups(planzone.uuid);
  const {
    isRequestPending: isProjectRequestPending,
    isRequestSuccess: isProjectRequestSuccess,
    isRequestFailure: isProjectRequestFailure,
    groups: projectGroups,
    retrieveResourceGroups: retrieveProjectResourceGroups,
  } = useRetrieveResourceGroups(isInProject ? ctx.project.uuid : project.uuid);
  const { projectResources } = useProjectResourcesRetrieval(
    isInProject ? ctx.project : project,
  );
  const [areGroupsToggled, setAreGroupsToggled] = React.useState(false);
  const { isRequestPending, allocateResources } = useAllocateResources(task);
  const { removeAllocation } = useRemoveAllocation({ ...allocation, uuid: "" });
  const allocMenu = (isPlaceholder: boolean) => (
    <ScreenWatcher
      renderChildren={(screen) => (
        <LayoutWatcher
          renderChildren={(layout) => {
            const maxHeight =
              typeof anchor.bottom !== "undefined"
                ? screen.height - anchor.bottom - 50
                : screen.height - layout.top - 50;

            return (
              <ResourceAllocationMenu
                allocatedResources={task.allocations.map(
                  (allocation) => allocation.resource,
                )}
                maxHeight={maxHeight}
                select={(resources) =>
                  !isRequestPending &&
                  allocateResources({
                    task,
                    project,
                    allocations: resources.map((resource) => ({
                      ...allocation,
                      resource,
                    })),
                  })
                }
                unSelect={(resources) =>
                  resources.forEach(({ uuid }) => {
                    const allocation = allocations.find(
                      (allocation) => allocation.resource.uuid === uuid,
                    );

                    allocation && removeAllocation(allocation);
                  })
                }
                openedViews={["resources"]}
              />
            );
          }}
        />
      )}
    />
  );
  const planzoneGroups = isInProject
    ? groups.reduce<Identifiable<G>[]>((filteredGroups, group) => {
        const filteredGroup = {
          ...group,
          resources: group.resources.filter(
            ({ uuid }) =>
              !!projectResources.find(({ resource }) => resource.uuid === uuid),
          ),
        };

        return filteredGroup.resources.length > 0
          ? [...filteredGroups, filteredGroup]
          : filteredGroups;
      }, [])
    : groups;

  React.useEffect(() => {
    !isPlanzoneRequestPending &&
      !isPlanzoneRequestSuccess &&
      !isPlanzoneRequestFailure &&
      retrievePlanzoneResourceGroups({
        namespace: planzone.uuid,
        resourceType: "planzone",
      });
  }, []);

  React.useEffect(() => {
    !isProjectRequestPending &&
      !isProjectRequestSuccess &&
      !isProjectRequestFailure &&
      retrieveProjectResourceGroups({
        namespace: isInProject ? ctx.project.uuid : project.uuid,
        resourceType: "project",
      });
  }, []);

  return allocations.length > 0 ? (
    <div className={styles.allocations}>
      <Header
        toggled={areGroupsToggled}
        toggle={() => setAreGroupsToggled((t) => !t)}
      />
      <div
        className={classNames(styles.main, {
          [styles.groupMain]: areGroupsToggled,
        })}
      >
        {areGroupsToggled ? (
          <React.Fragment>
            {sortByGroup(planzoneGroups, allocations, "planzone").map(
              (group) => (
                <>
                  <Group group={group} />
                  {[...group.allocations]
                    .sort((a, b) =>
                      a.resource.name.localeCompare(b.resource.name),
                    )
                    .sort((a, b) => {
                      const priority = {
                        [ResourceTypes.HUMAN]: 1,
                        [ResourceTypes.VIRTUAL]: 2,
                        [ResourceTypes.MATERIAL]: 3,
                      };

                      return (
                        priority[a.resource.type] - priority[b.resource.type]
                      );
                    })
                    .map((allocation) => (
                      <div
                        key={allocation.uuid}
                        className={classNames(styles.item, styles.groupItem)}
                      >
                        <Allocation
                          allocation={allocation}
                          isLastIncompleteAllocation={isLastIncompleteAllocation(
                            allocations,
                            allocation,
                          )}
                          task={task}
                          project={project}
                        />
                      </div>
                    ))}
                </>
              ),
            )}
            {sortByGroup(projectGroups, allocations, "project").map((group) => (
              <>
                <Group group={group} />
                {[...group.allocations]
                  .sort((a, b) =>
                    a.resource.name.localeCompare(b.resource.name),
                  )
                  .sort((a, b) => {
                    const priority = {
                      [ResourceTypes.HUMAN]: 1,
                      [ResourceTypes.VIRTUAL]: 2,
                      [ResourceTypes.MATERIAL]: 3,
                    };

                    return (
                      priority[a.resource.type] - priority[b.resource.type]
                    );
                  })
                  .map((allocation) => (
                    <div
                      key={allocation.uuid}
                      className={classNames(styles.item, styles.groupItem)}
                    >
                      <Allocation
                        allocation={allocation}
                        isLastIncompleteAllocation={isLastIncompleteAllocation(
                          allocations,
                          allocation,
                        )}
                        task={task}
                        project={project}
                      />
                    </div>
                  ))}
              </>
            ))}
          </React.Fragment>
        ) : (
          <>
            {[...allocations]
              .sort((a, b) => a.resource.name.localeCompare(b.resource.name))
              .sort((a, b) => {
                const priority = {
                  [ResourceTypes.HUMAN]: 1,
                  [ResourceTypes.VIRTUAL]: 2,
                  [ResourceTypes.MATERIAL]: 3,
                };

                return priority[a.resource.type] - priority[b.resource.type];
              })
              .map((allocation) => (
                <div key={allocation.uuid} className={styles.item}>
                  <Allocation
                    allocation={allocation}
                    isLastIncompleteAllocation={isLastIncompleteAllocation(
                      allocations,
                      allocation,
                    )}
                    task={task}
                    project={project}
                  />
                </div>
              ))}
          </>
        )}
      </div>
      {!readOnly && (
        <SelectList
          rootClassName={styles.addAllocation}
          listClassName={classNames(
            styles.list,
            typeof anchor.bottom !== "undefined" ? styles.top : styles.bottom,
          )}
        >
          {() => ({
            input: (
              <div className={styles.iconBox}>
                <AddAllocationIcon className={styles.icon} />
              </div>
            ),
            list: allocMenu(false),
          })}
        </SelectList>
      )}
    </div>
  ) : (
    <div className={styles.allocations}>
      <p className={styles.placeholder}>
        {t("taskPage.allocations.placeholder")}
      </p>
      {allocMenu(true)}
    </div>
  );
}

export { Details };
